import React, { useEffect } from "react";
import { Coins } from "lucide-react";
import { create } from "zustand";
import { getCoinGeckoUrl } from "@/constants/urls.constants";

// Define the store interface
interface PriceStore {
	solPrice: number | null;
	lastUpdated: number | null;
	isLoading: boolean;
	error: string | null;
	fetch: () => Promise<void>;
}

// Properly typed Zustand store
const usePriceStore = create<PriceStore>((set, get) => ({
	solPrice: null,
	lastUpdated: null,
	isLoading: false,
	error: null,
	fetch: async () => {
		const now = Date.now();
		const state = get();
		const lastUpdated = state.lastUpdated;

		// Cache for 5 minutes
		if (!lastUpdated || now - lastUpdated > 5 * 60 * 1000) {
			set({ isLoading: true, error: null });
			try {
				const url = getCoinGeckoUrl();
				const response = await fetch(url);
				if (!response.ok) throw new Error("Failed to fetch SOL price");
				const data = await response.json();
				// Check if we have the expected data structure
				if (data && data.solana && typeof data.solana.usd === "number") {
					set({
						solPrice: data.solana.usd,
						lastUpdated: now,
						isLoading: false,
					});
				} else {
					console.error("Unexpected data structure:", data);
					throw new Error("Invalid data structure from API");
				}
			} catch (error) {
				console.error("Error fetching SOL price:", error);
				set({
					error: "Unable to fetch price",
					isLoading: false,
				});
			}
		}
	},
}));

// Define component props interface
interface NFTPriceProps {
	priceInUSD: number;
	showIcon?: boolean;
	containerClassName?: string;
	iconClassName?: string;
	textClassName?: string;
}

const NFTPrice: React.FC<NFTPriceProps> = ({
	priceInUSD,
	showIcon = true,
	containerClassName = "flex items-center space-x-2",
	iconClassName = "text-mgt-yellow size-4",
	textClassName = "text-sm",
}) => {
	const { solPrice, isLoading, error, fetch } = usePriceStore();

	useEffect(() => {
		fetch();
	}, [fetch]);

	const solAmount = solPrice ? (priceInUSD / solPrice).toFixed(2) : null;

	return (
		<div className={`flex items-center gap-2 ${containerClassName || ""}`.trim()}>
			{showIcon && <Coins className={iconClassName} />}
			<p className={textClassName}>
				{isLoading ? (
					"Loading..."
				) : error || !solAmount ? (
					`$${priceInUSD} USD`
				) : (
					<>
						{solAmount} SOL (${priceInUSD})
					</>
				)}
			</p>
		</div>
	);
};

export default NFTPrice;
